import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, Subscription, throwError } from 'rxjs';
import SockJS from 'sockjs-client';
import { HttpClient, HttpParams } from '@angular/common/http';
import { catchError, tap } from 'rxjs/operators';
import { AppUserDTO } from '../_models/userDTO';
import { environment } from '../../../environments/environment';

import { Client, IMessage, Stomp, StompSubscription } from '@stomp/stompjs';
import { CommonResponse } from '../_models/Response';

export interface MessageDTO {
  deletedForSender: boolean;
  deletedForReceiver: boolean;
  content: string;
  senderId: number;
  recipientId: number;
  roomId: string;
  seen: boolean;
  // ajoute timestamp si tu l'envoies du backend (à adapter)
  timestamp?: string;
}


export interface Message {
  id: number;
  content: string;
  sender?: AppUserDTO;
  recipient?: AppUserDTO;
  chatRoom: ChatRoom;
  timestamp: string;
  seen: boolean;
}

export interface MessageRequest {
  content: string;
  senderId: number;
  recipientId: number;
  roomId: string;
  timestamp: string;
  seen: boolean;
}

export interface ChatRoom {
  id?: number;
  roomId: string;
  sender?: AppUserDTO;
  receiver?: AppUserDTO;
}


@Injectable({
  providedIn: 'root',
})
export class ChatService {
  private messagesSubject = new BehaviorSubject<Message[]>([]);
  private connectedSubject = new BehaviorSubject<boolean>(false);
  private unreadCountsSubject = new BehaviorSubject<{ [userId: number]: number }>({});
  private subscriptions: StompSubscription[] = [];
  private currentRoomId: string | null = null;
  private unreadCounts = new Map<number, number>();
  private stompClient: Client;
  private notificationSubject = new Subject<string>();
  private wsEndpoint = `${environment.base_url}/ws-chat`;
  private baseUrl = environment.base_url;
  private apiUrl = `${this.baseUrl}/api/chat`;
  private messageReceivedSubject = new Subject<Message>();
  public messageReceived$ = this.messageReceivedSubject.asObservable();
  public unreadCounts$ = this.unreadCountsSubject.asObservable();
  public notifications$ = this.notificationSubject.asObservable();
  private messageSubject = new Subject<Message>();
  private notificationReceivedSubject = new Subject<any>();
  public notificationReceived$ = this.notificationReceivedSubject.asObservable();
  private rxSubscriptions: Subscription[] = [];

  constructor(private http: HttpClient) {
    // Nouvelle logique connect + fetch automatique
    //this.connectWithSockJS();
    const userId = Number(sessionStorage.getItem('user_id'));
    if (userId) {
      this.fetchUnreadMessages(userId);
    }
  }

  currentUser: { username: string } | null = null;

connect(username: string): Promise<void> {
  this.currentUser = { username };

  if (this.stompClient && this.stompClient.connected) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    if (!this.currentUser) {
      console.error('❌ currentUser non défini, impossible de se connecter');
      return reject('currentUser non défini');
    }

    const socket = new SockJS(`${this.wsEndpoint}?username=${this.currentUser.username}`); // ✅ SockJS + username

    this.stompClient = new Client({
      webSocketFactory: () => socket, // ✅ remplace brokerURL
      connectHeaders: {},
      debug: (str) => console.log('STOMP:', str),
      reconnectDelay: 5000,
    });

    this.stompClient.onConnect = () => {
      console.log('✅ WebSocket connecté');
      this.connectedSubject.next(true);

      this.stompClient.subscribe('/user/queue/new-message-notification', (message) => {
        const notifBody = message.body;
        console.log('🔔 Nouvelle notification reçue :', notifBody);
        this.notificationReceivedSubject.next(notifBody);
      });

      const msgSub = this.stompClient.subscribe('/user/queue/messages', (msg: IMessage) => {
        const body = JSON.parse(msg.body);
        this.messageReceivedSubject.next(body);
        this.handleIncomingMessage(body);
      });

      this.subscriptions.push(msgSub);
      resolve();
    };

    this.stompClient.onStompError = (frame) => {
      console.error('❌ Erreur STOMP:', frame.headers['message']);
      reject(frame.headers['message']);
    };

    this.stompClient.onWebSocketError = (evt) => {
      console.error('❌ Erreur WebSocket SockJS :', evt);
      reject(evt);
    };

    this.stompClient.activate();
  });
}
connectWithSockJS(): void {
  const socket = new SockJS(this.wsEndpoint);
  this.stompClient = new Client({
    webSocketFactory: () => socket,
    reconnectDelay: 5000,
    debug: (str) => console.log('STOMP:', str),
    connectHeaders: {
      username: sessionStorage.getItem('username') || ''
    },
    onConnect: () => {
      console.log('✅ Connexion WebSocket avec SockJS réussie');

      // Souscription aux messages privés
      this.stompClient.subscribe('/user/queue/messages', (msg: IMessage) => {
        const body: Message = JSON.parse(msg.body);
        this.messageReceivedSubject.next(body);
        this.handleIncomingMessage(body);
      });
    },
    onStompError: (frame) => {
      console.error('❌ Erreur STOMP SockJS :', frame);
    },
    onWebSocketError: (evt) => {
      console.error('❌ Erreur WebSocket SockJS :', evt);
    }
  });

  this.stompClient.activate(); // 🚀 C’est ceci qui remplace `connect()`
}

  disconnect(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.subscriptions = [];
    this.rxSubscriptions.forEach((sub) => sub.unsubscribe());
    this.rxSubscriptions = [];

    if (this.stompClient) {
      this.stompClient.deactivate();
      this.connectedSubject.next(false);
      console.log('🔴 WebSocket déconnecté');
    }
  }

  addSubscription(sub: Subscription) {
    this.rxSubscriptions.push(sub);
  }

  sendMessageWithNotification(msg: MessageRequest): void {
    console.log('⏳ Tentative d\'envoi de message avec notification...', msg);

    if (!this.stompClient || !this.stompClient.connected) {
      console.error('❌ STOMP client non connecté');
      return;
    }

    try {
      this.stompClient.publish({
        destination: '/app/chat.send',
        body: JSON.stringify(msg),
        headers: { 'content-type': 'application/json' },
      });
      console.log('📤 Message principal envoyé avec succès');
    } catch (error) {
      console.error('❌ Erreur envoi message :', error);
    }

    if (msg.recipientId) {
      const notification = {
        senderId: msg.senderId,
        receiverId: msg.recipientId,
        content: `Nouveau message de ${msg.senderId}`,
        timestamp: new Date().toISOString(),
      };

      try {
        this.stompClient.publish({
          destination: '/app/chat.notify',
          body: JSON.stringify(notification),
          headers: { 'content-type': 'application/json' },
        });
        console.log('📤 Notification envoyée');
      } catch (error) {
        console.error('❌ Erreur envoi notification :', error);
      }
    }
  }

  getLastMessages(userId: number): Observable<{ [chatRoomId: number]: MessageDTO }> {
    return this.http.get<{ [chatRoomId: number]: MessageDTO }>(`${this.apiUrl}/last-messages/${userId}`);
  }

  getMessagesValue(): Message[] {
    return this.messagesSubject.getValue();
  }

  public getMessages() {
    return this.messageSubject.asObservable();
  }

  handleIncomingMessage(message: Message) {
    const currentUserId = Number(sessionStorage.getItem('user_id'));
    const fromId = message.sender?.id;

    if (fromId != null && fromId !== currentUserId) {
      const previous = this.unreadCounts.get(fromId) || 0;
      this.unreadCounts.set(fromId, previous + 1);
      this.unreadCountsSubject.next(Object.fromEntries(this.unreadCounts));
      this.notificationSubject.next(`Nouveau message de ${message.sender?.name}`);
    }
  }

  fetchUnreadMessages(currentUserId: number): void {
    this.http.get<Message[]>(`${this.apiUrl}/unread?userId=${currentUserId}`).subscribe({
      next: (messages) => {
        const counts: { [userId: number]: number } = {};
        messages.forEach((msg) => {
          const senderId = msg.sender?.id;
          if (senderId != null) {
            counts[senderId] = (counts[senderId] || 0) + 1;
          }
        });
        this.unreadCounts = new Map(Object.entries(counts).map(([k, v]) => [Number(k), v]));
        this.unreadCountsSubject.next(counts);
      },
      error: (err) => console.error('❌ Erreur fetchUnreadMessages :', err),
    });
  }

  clearUnreadCount(senderId: number): void {
    const current = { ...this.unreadCountsSubject.value };
    current[senderId] = 0;
    this.unreadCountsSubject.next(current);
  }


  joinRoom(roomId: string): void {
    if (this.currentRoomId) {
      this.subscriptions.forEach((sub) => sub.unsubscribe());
      this.subscriptions = [];
    }

    this.currentRoomId = roomId;

    const sub = this.stompClient.subscribe(`/topic/messages/${roomId}`, (msg: IMessage) => {
      const message: Message = JSON.parse(msg.body);

      this.addMessage(message);
    });

    this.subscriptions.push(sub);
  }

  sendMessage(msg: MessageRequest): void {
    if (!this.stompClient || !this.stompClient.connected) {
      console.error('STOMP client non connecté');
      return;
    }

    this.stompClient.publish({
      destination: '/app/chat.send',
      body: JSON.stringify(msg),
    });
  }

  onMessage(): Observable<Message[]> {
    return this.messagesSubject.asObservable();
  }

  private addMessage(message: Message): void {
    const currentMessages = this.messagesSubject.getValue();
    this.messagesSubject.next([...currentMessages, message]);
    this.buildChatRooms();
  }

  getMessagesByRoom(roomId: string): Observable<Message[]> {
    return this.http.get<Message[]>(`${this.apiUrl}/room/${roomId}`).pipe(
      tap((messages) => this.messagesSubject.next(messages)),
      catchError((error) => {
        console.error('Erreur getMessagesByRoom:', error);
        return throwError(() => error);
      })
    );
  }

  delete(messageId: number, currentUserId: number): Observable<any> {
  return this.http.put(`${this.apiUrl}/delete-message/${messageId}?userId=${currentUserId}`, { });
}

  public chatRooms: ChatRoom[] = [];

  private buildChatRooms(): void {
    const messages = this.messagesSubject.getValue();
    const roomsMap = new Map<string, ChatRoom>();

    for (const msg of messages) {
      if (msg.chatRoom && !roomsMap.has(msg.chatRoom.roomId)) {
        roomsMap.set(msg.chatRoom.roomId, msg.chatRoom);
      }
    }

    this.chatRooms = Array.from(roomsMap.values());
  }
}
